import { useCallback, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { ModalDialog } from 'core-components/library/modalDialog'
import {
  FinancialPlanQualityPillar,
  FinancialPlanScoreInfo,
  type IFinancialPlanQuality,
} from 'partner360/features/financialPlanQuality'
import { TRANSLATION_KEYS } from 'partner360/features/financialPlanQuality/configs/translations'

import type { ICockpitExportView } from '../../../../api'
import {
  type TCockpitExportSlot,
  useFillCockpitExportSlot,
} from '../../../components/cockpitExport'

import {
  getFinSobQualityPillarKeys,
  getFinSobQualityPillarsExport,
  type TFinSobQualityOpenDetails,
} from './getFinSobQualityPillarsExport'

import styles from './CockpitFinSobQualityPillars.module.scss'

interface ICockpitFinSobQualityPillarsProps {
  quality: IFinancialPlanQuality
  exportSlot?: TCockpitExportSlot
}

/**
 * A single partner's Financial & SOB Plan Quality: the partner page's own pillar cards, laid out
 * as `FinancialPlanQualityContent` lays them out. Composed here rather than through that component
 * so the cards can report which details table is open, which is all the export needs to repeat
 * them.
 */
export const CockpitFinSobQualityPillars = ({
  quality,
  exportSlot,
}: ICockpitFinSobQualityPillarsProps) => {
  const translate = useSafetyTranslation()
  const [isScoreInfoVisible, setIsScoreInfoVisible] = useState(false)
  const [openDetails, setOpenDetails] = useState<TFinSobQualityOpenDetails>({
    financialGrowth: false,
    sobGrowth: false,
  })

  const handleFinancialDetails = useCallback(
    (isOpen: boolean) => setOpenDetails((open) => ({ ...open, financialGrowth: isOpen })),
    []
  )
  const handleSobDetails = useCallback(
    (isOpen: boolean) => setOpenDetails((open) => ({ ...open, sobGrowth: isOpen })),
    []
  )

  const exportView = useCallback(
    (): ICockpitExportView => ({
      key: 'finSobQuality',
      controls: [],
      pillars: getFinSobQualityPillarsExport({ quality, openDetails, translate }),
    }),
    [quality, openDetails, translate]
  )

  useFillCockpitExportSlot(exportSlot, exportView)

  return (
    <>
      <div className={styles.pillars}>
        {getFinSobQualityPillarKeys(quality).map((key) => (
          <FinancialPlanQualityPillar
            key={key}
            title={TRANSLATION_KEYS.quality[key].title}
            description={TRANSLATION_KEYS.quality[key].description}
            pillar={quality[key]}
            onHowScore={() => setIsScoreInfoVisible(true)}
            onDetailsOpenChange={
              key === 'financialGrowth' ? handleFinancialDetails : handleSobDetails
            }
          />
        ))}
      </div>

      <ModalDialog
        show={isScoreInfoVisible}
        onClose={() => setIsScoreInfoVisible(false)}
        content={() => <FinancialPlanScoreInfo />}
        size={'xs'}
      />
    </>
  )
}

CockpitFinSobQualityPillars.displayName = 'CockpitFinSobQualityPillars'
